import React, { useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Iconsax, matching Inbox's rail (app-wide standard, see icon-library-iconsax
// memory) -- replaces the old @mui/icons-material set. One-for-one semantic
// swap, no new/removed items.
import {
  Setting4,
  Profile,
  Airdrop,
  // Crown1, // only used by the commented-out "Plans" sidebar entry below
  UserSquare,
  Profile2User,
  SecurityUser,
  FlashCircle,
  TrendUp,
  Notification,
  ShieldTick,
  UserEdit,
} from "iconsax-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { fetchUserPermissions, fetchUserDetails } from "../../redux/Slice/User/userDetailsSlice";
import PanelHeading, { SUBTITLE_HEIGHT } from "../Navbar/PanelHeading";
import SidebarNavList from "../Navbar/SidebarNavList";

// icon is a component reference here (not a built element) since none of
// these need a per-item color/variant computation beyond the standard
// selected/unselected swap -- that swap happens once below, in optionToItem.
// description is exported alongside for Settings.jsx's content-header
// subtitle, so the sidebar icon and the header icon/description come from
// one shared list instead of two independently-maintained copies.
export const options = [
  { name: "General", Icon: Setting4, description: "Manage your account's basic information." },
  { name: "Profile", Icon: Profile, description: "Update your personal profile details." },
  { name: "Integrations", Icon: Airdrop, description: "Connect and manage third-party integrations." },
  // "Plans" sidebar entry commented out, not deleted — ClusterDen's own in-app plan page is
  // removed for the Shopify App Store billing-compliance fix (1.2.1); Shopify App Pricing
  // owns plan selection. Left visible it opened the General page (SettingsDetails.jsx no
  // longer renders Plans), which looks broken to a reviewer.
  // { name: "Plans", Icon: Crown1, description: "View and manage your subscription plan." },
  { name: "Teams", Icon: UserSquare, description: "Organize team members into teams." },
  { name: "Team Members", Icon: Profile2User, description: "Manage who has access to this account." },
  { name: "Roles & Permission", Icon: SecurityUser, description: "Control what each role can see and do." },
  { name: "Quick Replies", Icon: FlashCircle, description: "Create reusable message templates." },
  { name: "Lifecycle", Icon: TrendUp, description: "Configure contact lifecycle stages." },
  { name: "Contact Profile", Icon: UserEdit, description: "Add custom profile fields for your contacts." },
  { name: "Notification", Icon: Notification, description: "Manage notification preferences and sounds." },
  { name: "Security", Icon: ShieldTick, description: "Manage account security settings." },
];

const SettingsOptions = ({ onOptionClick }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const selectedOption = useSelector((state) => state.settings.selectedOption) || "General";
  const currentWorkspace = useSelector((state) => state.workspace?.currentWorkspace);
  const workspaces = useSelector((state) => state.workspace?.workspaces) || [];
  const userPermissionsPayload = useSelector((state) => state.userDetails.userPermissions);
  const userDetails = useSelector((state) => state.userDetails.userDetails);

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(fetchUserDetails());
  }, [dispatch]);

  const visibleOptions = useMemo(() => {
    if (userPermissionsPayload == null) return options;
    const perms = userPermissionsPayload.permissions || {};
    const sec = perms.Security || {};
    const roleNm = userDetails?.roleName;
    return options.filter((o) => {
      if (o.name === "Security") return sec.View === true || roleNm === "Owner";
      if (o.name === "Integrations") return roleNm === "Owner" || perms.Integrations?.View === true;
      if (o.name === "Plans") return roleNm === "Owner";
      if (o.name === "Contact Profile") return roleNm === "Owner" || perms.ContactProfileFields?.View === true;
      return true;
    });
  }, [userPermissionsPayload, userDetails]);

  const handleSelectOption = (optionName, option) => {
    if (option?.navigateTo === "api-integration") {
      const workspaceId = currentWorkspace?._id || (workspaces[0]?._id);
      if (workspaceId && accountid) {
        navigate(`/home/${accountid}/workspaces/${workspaceId}/api-integration`);
      }
      return;
    }
    dispatch(setSelectedOption(optionName));
    if (onOptionClick) {
      onOptionClick();
    }
  };

  const items = visibleOptions.map((option) => {
    const selected = selectedOption === option.name;
    const Icon = option.Icon;
    return {
      key: option.name,
      label: option.name,
      selected,
      onClick: () => handleSelectOption(option.name, option),
      icon: (
        <Icon
          size="100%"
          color={selected ? theme.palette.text.primary : "currentColor"}
          variant={selected ? "Bold" : "Linear"}
        />
      ),
    };
  });

  return (
    <Box sx={{ height: "100%", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      {/* Forced to the same fixed height as the content pane's header (which has
          a subtitle, this one doesn't) so their bottom borders land on the exact
          same line instead of two independently-computed heights drifting apart
          by a couple of px (Rahim, 2026-09-10: "match the lines perfect"). */}
      <PanelHeading title="Settings" variant="narrow" sx={{ height: SUBTITLE_HEIGHT }} />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          // No explicit background -- inherits the sidebar wrapper's
          // background.default, matching Inbox's rail exactly.
          overflowY: "auto",
          scrollbarWidth: { xs: "none", sm: "none", md: "thin", lg: "thin", xl: "thin" },
          msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" },
          "&::-webkit-scrollbar": {
            width: { xs: 0, sm: 0, md: "3px", lg: "3px", xl: "3px" },
          },
        }}
      >
        <SidebarNavList items={items} />
      </Box>
    </Box>
  );
};

export default SettingsOptions;
